import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  Fuel, Sparkles, Send, CheckCircle2, MessageCircle, ArrowRight, Play,
  ShieldCheck, Zap, Users,
} from "lucide-react";

const ROLES = [
  { key: "dealer", label: "Pump Dealer / Owner", emoji: "👑" },
  { key: "manager", label: "Manager", emoji: "🧑‍💼" },
  { key: "staff", label: "Salesman / Pump Staff", emoji: "⛽" },
  { key: "curious", label: "Just curious", emoji: "🤔" },
  { key: "other", label: "Something else", emoji: "✨" },
];

/**
 * Landing page for Google sign-ins where the email isn't invited yet.
 * Turns the "you're not invited" rejection into a warm lead-capture flow.
 * On submit → creates a lead in the backend → hands off to WhatsApp on the
 * founder's number with a pre-filled intro message.
 */
export default function HelloThere() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const google = useMemo(() => ({
    email: params.get("email") || "",
    name: params.get("name") || "",
    picture: params.get("picture") || "",
    google_id: params.get("google_id") || "",
  }), [params]);

  const [role, setRole] = useState("dealer");
  const [pumpName, setPumpName] = useState("");
  const [city, setCity] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const canSubmit = google.email && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/leads/from-google", {
        email: google.email,
        name: google.name,
        picture: google.picture,
        google_id: google.google_id,
        role,
        pump_name: pumpName,
        city,
        phone,
        message,
      });
      setResult(data);
      toast.success("Got it! Now let's continue on WhatsApp.");
      // Auto-open WhatsApp after a short pause so user sees the confirmation
      setTimeout(() => {
        if (data.wa_link) window.open(data.wa_link, "_blank", "noopener,noreferrer");
      }, 900);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative flex min-h-screen flex-col items-center bg-background px-4 py-10">
      {/* Ambient background */}
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-grid-fade opacity-60" />
      <div aria-hidden className="pointer-events-none absolute -left-24 top-1/3 h-72 w-72 rounded-full bg-primary/15 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -right-16 top-10 h-64 w-64 rounded-full bg-emerald-500/15 blur-3xl" />

      <div className="relative z-10 mx-auto w-full max-w-lg">
        {/* Brand */}
        <Link to="/" className="mb-6 inline-flex items-center gap-2.5 text-foreground">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-md">
            <Fuel className="h-4 w-4" />
          </span>
          <span className="font-display text-lg font-bold tracking-tight">{t("hellothere.hisabx")}</span>
        </Link>

        {!result ? (
          <>
            {/* Warm welcome */}
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">
              <Sparkles className="h-3 w-3" /> Welcome, glad you're here
            </div>
            <h1 className="font-display text-3xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl">
              Hey{google.name ? " " + google.name.split(" ")[0] : ""}!<br />
              Let's get you into HisabX. 🚀
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              HisabX is invite-only right now (we&apos;re onboarding a limited cohort of founding dealers).
              Tell me a bit about your pump and I'll personally get you in on WhatsApp
              — usually within a few hours.
            </p>

            {/* Google identity card */}
            {google.email && (
              <div className="mt-5 flex items-center gap-3 rounded-xl border border-border/60 bg-card/60 p-3 backdrop-blur-sm" data-testid="hello-google-card">
                {google.picture ? (
                  <img src={google.picture} alt="" className="h-10 w-10 rounded-full border border-border" />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 text-primary">
                    <Users className="h-5 w-5" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold text-foreground">{google.name || "New visitor"}</div>
                  <div className="truncate text-xs text-muted-foreground">{google.email}</div>
                </div>
                <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400">
                  Google verified
                </span>
              </div>
            )}

            {/* Form */}
            <div className="mt-6 space-y-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  You are a...
                </Label>
                <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {ROLES.map((r) => (
                    <button
                      key={r.key}
                      type="button"
                      onClick={() => setRole(r.key)}
                      data-testid={`hello-role-${r.key}`}
                      className={
                        "flex items-center gap-1.5 rounded-lg border-2 px-2.5 py-2 text-left text-xs transition " +
                        (role === r.key
                          ? "border-primary bg-primary/10 font-semibold text-foreground"
                          : "border-border/60 bg-background/40 text-muted-foreground hover:border-primary/40")
                      }
                    >
                      <span>{r.emoji}</span>
                      <span className="min-w-0 flex-1 truncate">{r.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="pump" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Pump name
                  </Label>
                  <Input
                    id="pump"
                    placeholder="e.g. Shree Krishna BPCL"
                    value={pumpName}
                    onChange={(e) => setPumpName(e.target.value)}
                    className="mt-1 h-11"
                    data-testid="hello-pump-name"
                  />
                </div>
                <div>
                  <Label htmlFor="city" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    City
                  </Label>
                  <Input
                    id="city"
                    placeholder="e.g. Indore"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="mt-1 h-11"
                    data-testid="hello-city"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  WhatsApp number (India)
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  inputMode="numeric"
                  placeholder="98765 43210"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  className="mt-1 h-11"
                  data-testid="hello-phone"
                />
              </div>

              <div>
                <Label htmlFor="msg" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Anything you want to add? <span className="normal-case text-muted-foreground/60">(optional)</span>
                </Label>
                <textarea
                  id="msg"
                  rows={2}
                  placeholder="e.g. 4-nozzle petrol pump, need to fix shortage tracking"
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, 400))}
                  className="mt-1 w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="hello-message"
                />
              </div>

              <Button
                onClick={submit}
                disabled={!canSubmit}
                className="mt-2 h-12 w-full bg-primary text-base font-bold shadow-md shadow-primary/25"
                data-testid="hello-submit"
              >
                {submitting ? "Sending…" : (
                  <>{t("hellothere.continue_on_whatsapp")} <ArrowRight className="ml-1 h-4 w-4" /></>
                )}
              </Button>

              <p className="text-center text-[11px] leading-relaxed text-muted-foreground/70">
                By continuing, you agree that we'll reach out on WhatsApp / email.
                We don't spam — you're one message away from unsubscribing anytime.
              </p>
            </div>

            {/* Why HisabX — soft pitch below the fold */}
            <div className="mt-10 grid gap-3 sm:grid-cols-3">
              <PitchTile icon={ShieldCheck} title={t("hellothere.shortage_proof")} sub="Every rupee audited, every reading locked" />
              <PitchTile icon={Zap} title="5-min shift close" sub="No more paper HISAB or Excel drama" />
              <PitchTile icon={MessageCircle} title={t("hellothere.whatsapp_first")} sub="Alerts land where you already are" />
            </div>

            <div className="mt-8 flex flex-col items-center gap-2">
              <Link
                to="/download"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
                data-testid="hello-see-app"
              >
                <Play className="h-3.5 w-3.5" /> See the app in action
              </Link>
              <Link to="/login" className="text-[11px] text-muted-foreground hover:underline" data-testid="hello-back">
                Already invited? Sign in →
              </Link>
            </div>
          </>
        ) : (
          <SuccessCard result={result} name={google.name} />
        )}
      </div>
    </div>
  );
}

function PitchTile({ icon: Icon, title, sub }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card/40 p-3 backdrop-blur-sm">
      <Icon className="mb-1.5 h-4 w-4 text-primary" />
      <div className="text-sm font-bold text-foreground">{title}</div>
      <div className="mt-0.5 text-[11px] leading-tight text-muted-foreground">{sub}</div>
    </div>
  );
}

function SuccessCard({ result, name }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border-2 border-emerald-500/30 bg-emerald-500/5 p-6 text-center" data-testid="hello-success">
      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg shadow-emerald-500/30">
        <CheckCircle2 className="h-7 w-7" />
      </div>
      <h2 className="font-display text-2xl font-bold text-foreground">
        Perfect{name ? ", " + name.split(" ")[0] : ""}! 🙏
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Your details are saved. WhatsApp should have opened with a message
        pre-filled for you. Just hit send — I'll reply within a few hours.
      </p>

      <a
        href={result.wa_link}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-5 inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 text-base font-bold text-white shadow-md shadow-emerald-500/25 hover:bg-emerald-700"
        data-testid="hello-open-wa"
      >
        <Send className="h-4 w-4" /> Open WhatsApp again
      </a>

      <div className="mt-4 rounded-lg bg-background/60 p-3 text-left text-[11px] text-muted-foreground">
        <div className="mb-1 font-semibold uppercase tracking-wider">{t("hellothere.while_you_wait")}</div>
        <ul className="space-y-1">
          <li>📄 <Link to="/download" className="text-primary hover:underline">{t("hellothere.peek_at_the_app_features")}</Link></li>
          <li>📱 Number I&apos;ll message you from: <b className="text-foreground">+91 {result.founder_wa?.slice(-10).replace(/(\d{5})(\d{5})/, "$1 $2")}</b></li>
        </ul>
      </div>
    </div>
  );
}
